import axios from "axios";
import { Constants, CookieService, Utils } from "../../../shared";

export const RfpService = {
  /**
    * This is a function called `getRfpList` that takes a `campaignId` parameter. It uses the `axios`
    library to make an HTTP GET request to a specific URL constructed using the
    `Constants.CampaignDataUrl` and the `campaignId` parameter. It also includes a header with an
    `ansid` value obtained from a cookie using the `CookieService.getCookie` method.
     * @param {String} campaignId - id of campaign object
     * @returns {Promise} returns - axios promise
     */
  getRfpList: (campaignId, params = {}) => {
    let queryParams = {...params, sort: 'Ascending'}
    return axios
      .get(`${Constants.CampaignDataUrl}/${campaignId}/rfps`, {params:queryParams, headers: { ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid") }})
      .then(
        (response) => {return response && response.data;},
        (error) => {return Promise.reject(error);}
      );
  },

  /**
   * Retrieves RFP details.
   * @param {string} campaignId - The ID of the campaign.
   * @param {string} rfpId - The ID of the RFP.
   * @returns {Promise<Object>} A promise that resolves to the RFP details.
   *  If successful, the response data will be returned.
   *  Otherwise, an error will be rejected.
   */
  getRfpDetails: (campaignId, rfpId) => {
    return axios
      .get(`${Constants.CampaignDataUrl}/${campaignId}/rfps/${rfpId}?sort=Ascending`,{headers: { ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid") }})
      .then(
        (response) => {return response && response.data;},
        (error) => {return Promise.reject(error);}
      );
  },

  /**
     * This is a function called `createRfp` that takes two parameters: `payload` and `campaignId`. It
    uses the `axios` library to make an HTTP POST request to a specific URL constructed using the
    `Constants.CampaignDataUrl`, the `campaignId` parameter, and the `id` query parameter set to the
    `campaignId` parameter. It also includes a header with an `ansid` value obtained from a cookie
    using the `CookieService.getCookie` method. The `payload` parameter is included in the request
    body. 
     * @param {json} payload 
     * @param {string} campaignId -id of campaign
     * @returns {Promise} return- creates a new RFP (Request for Proposal) for a specific campaign.
     */
  createRfp: (payload, campaignId,editDailog) => {
    return axios.post(
      `${Constants.CampaignDataUrl}/${campaignId}/rfps?id=${campaignId}`,payload,
      {headers: {ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid")}}
    );
  },

  /**
 * Updates an RFP (Request for Proposal) with the given payload.
 * @param {string} rfpId - The ID of the RFP to update.
 * @param {object} payload - The data to update the RFP with.
 * @param {string} campaignId - The ID of the campaign associated with the RFP.
 * @returns {Promise} - A Promise that resolves to the updated RFP data.
 */
  updateRfp: (rfpId, payload, campaignId) => {
    return axios.put(`${Constants.CampaignDataUrl}/${campaignId}/rfps/${rfpId}?client-id=${Utils.getRouteParam("clientid")}`,payload,{headers: {ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid")}})
  },

  /**
   * `prepareCreateRfpPayload` is a function that takes two parameters: `formData` and `campaignId`. It prepares the payload data that will be sent in the HTTP POST request to create a new RFP (Request for Proposal) for a specific campaign.
   * @param {json} formData - data entered in the form while creating RFP
   * @param {String} camapignId - id of campaign
   * @param {json object} omniProfile - omniprofile -json - object with user profile details from omni
   * @returns {json object} return - create new RFP for specific campaign id
   */

  prepareCreateRfpPayload: (formData, camapignId, omniProfile,editDailog) => {
    let payload = {
      startDate: formData['Campaign start date'],
      endDate: formData['Campaign end date'],
      responseDueDate: formData['Due date'],
      rfpName: formData['rfpName'],
      campaignId: camapignId,
      recipients: formData['rfpEmails'].join(', '),
      includeFlighting: formData['Include flighting'],
      comments: formData['Comments'],
      vendorId: formData['Vendor id'],
      currencyCode: formData['Currency code'],
      budgets: [
        Number(formData['Budget 1']),
        Number(formData['Budget 2']),
        Number(formData['Budget 3'])
      ],
      providerId: '22-ZVW-1', // TODO: to be repalced with dynamic provider
      financialSupplier: 'Google', // TODO: to be replace with dynamic supplier
      strategy: 'Acquisition', // TODO: to be replace with dynamic strategy
      media: 'Online', // TODO to be replace with dynamic media value
      isDelete: 'False',
      created_by: omniProfile?.email,
      fileAttachments: formData['uploadedFiles']
    };
    if(editDailog){
      payload["updatedBy"] = omniProfile?.email
    }else{
      payload["created_by"] = omniProfile?.email
    }
    return payload;
  },

  /**
 * Prepares the payload for duplicating an RFP (Request for Proposal).
 * @param {Object} rfp - The RFP object to duplicate.
 * @param {Object} omniProfile - The Omni profile object.
 * @returns {Object} - The prepared payload for duplicating the RFP.
 */
  prepareDuplicateRfpPayload: (rfp, omniProfile) => {
    return {
      startDate: rfp.start_date,
      endDate: rfp.end_date,
      responseDueDate: rfp.response_due_date,
      rfpName: rfp.rfp_name,
      campaignId: rfp.amd_campaignid,
      recipients: rfp.recipients,
      includeFlighting: rfp.include_flighting,
      comments: rfp.comments,
      vendorId: rfp.vendor_id,
      currencyCode: rfp.currency_code,
      budgets: [
        rfp.budget1,
        rfp.budget2,
        rfp.budget3
      ],
      providerId: rfp.provider_id,
      financialSupplier: rfp.financial_supplier,
      strategy: rfp.strategy,
      media: rfp.media,
      isDelete: rfp.is_delete,
      created_by: omniProfile?.email,
    }
  },

  /**
   * Sends the RFP (Request for Proposal) to Media Ocean.
   * @param {Object} rfp - The RFP object to send.
   * @returns {Promise} - A promise that resolves with the response from the server.
   */
  sendToMediaOcean: (rfp, campaignObj) => {
    return axios.put(`${Constants.CampaignDataUrl}/${campaignObj.mediaocean_id}/rfps/${rfp.id}?client-id=${campaignObj.client_id}&platform=mediaocean`, null, { headers: { ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid") } })
  },

  /**
   * Description `prepareRfpEditFormData` is a function that takes an object `editRFPData` as a parameter. It
   * prepares and returns a new object with the data from `editRFPData` in a format that can be used to
   * pre-fill a form for editing an RFP (Request for Proposal). The function extracts specific
   * properties from `editRFPData` and formats them appropriately, such as splitting date strings and
   * converting boolean values to "yes" or "no" strings. The returned object can be used to pre-fill a
   * form with the existing RFP data for editing.
   * @param {object} editRFPData
   */
  prepareRfpEditFormData : (editRFPData) => {
    return {
      "Campaign start date": editRFPData.start_date?.split("T")[0],
      "Campaign end date": editRFPData.end_date?.split("T")[0],
      "Due date": editRFPData.response_due_date?.split("T")[0],
      "rfpName": editRFPData.rfp_name,
      "rfpEmails": editRFPData.recipients?.split(","),
      "Include flighting": editRFPData.include_flighting ? "yes" : "no",
      "Budget 1": editRFPData.budget1,
      "Budget 2": editRFPData.budget2,
      "Budget 3": editRFPData.budget3,
      "Financial supplier": editRFPData.financial_supplier,
      "Provider": editRFPData.provider_id,
      "Strategy": editRFPData.strategy,
      "Media": editRFPData.media,
      "Vendor id": editRFPData.vendor_id,
      "Currency code": editRFPData.currency_code,
      "Comments": editRFPData.comments,
      "uploadedFiles":  editRFPData.file_locations !== "" && editRFPData.file_locations?.split(",").map(location=>({filename: location.split("/")[location.split("/").length-1]})) || []
    }
  },

  /**
    * Get the proposals list with the given  campaign ID & RFP ID
    * @param {string} campaignId - The ID of the campaign associated with the RFP.
    * @param {string} rfpId - The ID of the RFP.
    * @returns {Promise} - A Promise that resolves with the proposal list.
  */
  getProposalsDetails: (campaignId, rfpId) => {
    return axios
      //.get(`${Constants.CampaignDataUrl}/planit-Hormel-24000145/rfps/45ghe/proposals`,{headers: { ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid") }})
      .get(`${Constants.CampaignDataUrl}/${campaignId}/rfps/${rfpId}/proposals`,{headers: { ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid") }})
      .then(
        (response) => {return response && response.data;},
        (error) => {return Promise.reject(error);}
      );
  },

  /**
    * Add the new proposals with the given  campaign ID & RFP ID
    * @param {string} campaignId - The ID of the campaign associated with the RFP.
    * @param {string} rfpId - The ID of the RFP.
    * @param {object} payload - Array of proposals object
    * @returns {Promise} - A Promise that resolves with the new proposal list.
  */
   addProposalsDetails: (campaignId, rfpId, payload) => {
    return axios
      .post(`${Constants.CampaignDataUrl}/${campaignId}/rfps/${rfpId}/proposals`,payload,{headers: { ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid") }})
      .then(
        (response) => {return response && response.data;},
        (error) => {return Promise.reject(error);}
      );
  },

  /**
    * Updates the list of proposals with the given payload
    * @param {string} campaignId - The ID of the campaign associated with the RFP.
    * @param {string} rfpId - The ID of the RFP.
    * @param {object} payload - Array of proposals object with the id, placements_id and param to be updated
    * @returns {Promise} - A Promise that resolves with the proposal list.
  */
  updateProposal: (campaignId, rfpId, payload) => {
    return axios.put(`${Constants.CampaignDataUrl}/${campaignId}/rfps/${rfpId}/proposals`,payload,{headers: {ansid: CookieService.getCookie('ANsid'), clientid: Utils.getRouteParam("clientid")}})
  },
};